import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import SaduTower from "@/components/SaduTower";

const FULL_STAGES = [
  { id: "strategy", label: "الاستراتيجية", path: "/app/strategy" },
  { id: "goals", label: "الأهداف والمؤشرات", path: "/app/goals" },
  { id: "execution", label: "التنفيذ", path: "/app/campaigns" },
  { id: "measure", label: "التوصيات والإغلاق", path: "/app/measurement" },
];

const READY_STAGES = [
  { id: "content", label: "توليد المحتوى", path: "/app/studio" },
  { id: "execution", label: "التنفيذ", path: "/app/campaigns" },
  { id: "measure", label: "التوصيات والإغلاق", path: "/app/measurement" },
];

const STANDALONE_STAGES = [
  { id: "content", label: "توليد المحتوى", path: "/app/studio" },
  { id: "ready", label: "جاهز للنشر", path: "/app/studio" },
];

export default function JourneyBar({ current, autoAdvance = true, onRegenerate = null, regenerating = false }) {
  const { activeProject } = useProjects();
  const navigate = useNavigate();
  const [raw, setRaw] = useState(null);
  const [notes, setNotes] = useState("");
  const [confirming, setConfirming] = useState(false);
  const prevDone = useRef(null);
  const advanced = useRef(false);

  const mode = activeProject?.standalone ? "standalone"
    : activeProject?.has_strategy_ready ? "ready" : "full";
  const STAGES = mode === "standalone" ? STANDALONE_STAGES : mode === "ready" ? READY_STAGES : FULL_STAGES;
  const r = raw || {};
  const confirmed = r.confirmed || {};
  // المرحلة لا تُعدّ منجزة إلا باكتمال مخرجاتها وتأكيد العميل لها
  const produced = mode === "standalone" ? { content: r.content, ready: r.content } : r;
  const flags = mode === "standalone" ? produced
    : Object.fromEntries(Object.keys(produced).map((k) => [k, !!produced[k] && !!confirmed[k]]));
  const needsConfirm = mode !== "standalone" && current && !!produced[current] && !confirmed[current];
  const confirmStage = async () => {
    setConfirming(true);
    try {
      await api.post(`/projects/${activeProject.id}/stages/${current}/confirm`, { notes });
      toast.success("تم تأكيد المرحلة");
      const { data } = await api.get(`/projects/${activeProject.id}/journey`);
      setRaw(data);
    } catch (e) { toast.error("تعذّر تأكيد المرحلة"); }
    finally { setConfirming(false); }
  };

  useEffect(() => {
    if (!activeProject) return;
    prevDone.current = null;
    advanced.current = false;
    setRaw(null);
    const refetch = () => api.get(`/projects/${activeProject.id}/journey`).then((r) => setRaw(r.data)).catch(() => {});
    refetch();
    const t = setInterval(refetch, 5000);
    return () => clearInterval(t);
  }, [activeProject]);

  // Auto-advance only when the stage transitions to done while the user is on it (creation flow);
  // opening an already-completed stage just shows its content.
  useEffect(() => {
    if (!raw || !autoAdvance || !activeProject || advanced.current || !current) return;
    const idx = STAGES.findIndex((s) => s.id === current);
    if (idx < 0 || idx >= STAGES.length - 1) return;
    const doneNow = !!flags[current];
    if (prevDone.current === false && doneNow) {
      const next = STAGES[idx + 1];
      if (next.path && next.path !== window.location.pathname) {
        advanced.current = true;
        toast.success(`اكتملت مرحلة «${STAGES[idx].label}» — ننتقل تلقائياً إلى «${next.label}»`);
        setTimeout(() => navigate(next.path), 1000);
      }
    }
    if (prevDone.current === null || !doneNow) prevDone.current = doneNow;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raw]);

  const curIdx = STAGES.findIndex((s) => s.id === current);
  const nextStage = curIdx >= 0 && curIdx < STAGES.length - 1 && flags[current] ? STAGES[curIdx + 1] : null;
  const showNext = nextStage && nextStage.path !== window.location.pathname;

  const completedCount = STAGES.filter((s) => flags[s.id]).length;
  const progress = Math.round((completedCount / STAGES.length) * 5);

  return (
    <div className="glass rounded-2xl p-4 sm:p-5 border border-border" data-testid="journey-bar">
      <div className="flex items-center gap-4 sm:gap-6">
        <SaduTower progress={progress} className="w-12 h-16 sm:w-16 sm:h-24 shrink-0 text-muted-foreground" />
        <div className="relative flex-1 min-w-0">
          <div className="overflow-x-auto pb-1 journey-scroll">
            <div className="flex items-start min-w-[320px]">
            {STAGES.map((s, i) => {
              const done = flags[s.id];
              const active = current === s.id;
              return (
                <React.Fragment key={s.id}>
                  <button
                    onClick={() => navigate(s.path)}
                    data-testid={`journey-stage-${s.id}`}
                    className="flex flex-col items-center gap-1.5 group shrink-0"
                  >
                    <motion.div
                      initial={false}
                      animate={{ scale: active ? 1.12 : 1 }}
                      className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors ${
                        done
                          ? "bg-primary border-primary text-white"
                          : active
                          ? "border-accent text-accent bg-accent/10"
                          : "border-border text-muted-foreground bg-secondary"
                      }`}
                    >
                      {done ? <Check className="w-4 h-4" /> : i + 1}
                    </motion.div>
                    <span
                      className={`text-[11px] sm:text-xs font-semibold whitespace-nowrap ${
                        active ? "text-accent" : done ? "text-foreground" : "text-muted-foreground"
                      }`}
                    >
                      {s.label}
                    </span>
                  </button>
                  {i < STAGES.length - 1 && (
                    <div className="flex-1 h-0.5 mx-1 sm:mx-2 mt-4 rounded-full bg-border relative min-w-[24px]">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: flags[STAGES[i + 1].id] ? "100%" : "0%" }}
                        transition={{ duration: 0.5 }}
                        className="absolute inset-y-0 right-0 bg-primary rounded-full"
                      />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
            </div>
          </div>
          <div className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-l from-card/90 to-transparent lg:hidden" />
        </div>
      </div>
      {needsConfirm && (
        <div className="mt-4 rounded-xl border border-accent/40 bg-accent/10 p-4 space-y-3" data-testid={`stage-confirm-${current}`}>
          <div className="text-sm font-bold text-accent">اكتملت مخرجات هذه المرحلة — راجعها ثم أكّدها للانتقال إلى المرحلة التالية</div>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-optional="true"
            placeholder="ملاحظاتك (اختياري): أي تعديل تريد تضمينه قبل التأكيد أو عند إعادة التوليد…"
            className="w-full rounded-lg bg-secondary border border-border px-3 py-2 text-sm" data-testid="stage-notes-input" />
          <div className="flex flex-wrap gap-2 justify-end">
            {onRegenerate && (
              <button onClick={() => onRegenerate(notes)} disabled={regenerating || confirming} data-testid="stage-regenerate-btn"
                className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary hover:bg-secondary/70 text-xs sm:text-sm font-bold px-4 py-2 transition-colors disabled:opacity-50">
                إعادة التوليد مع الملاحظات
              </button>
            )}
            <button onClick={confirmStage} disabled={confirming || regenerating} data-testid="stage-confirm-btn"
              className="inline-flex items-center gap-2 rounded-full bg-rose-600 hover:bg-rose-700 text-white text-xs sm:text-sm font-bold px-4 py-2 transition-colors disabled:opacity-50">
              <Check className="w-4 h-4" /> {confirming ? "جارٍ التأكيد…" : "أكّد المرحلة وانتقل"}
            </button>
          </div>
        </div>
      )}
      {showNext && (
        <div className="flex justify-end mt-3">
          <button onClick={() => navigate(nextStage.path)} data-testid="journey-next-btn"
            className="inline-flex items-center gap-2 rounded-full bg-primary hover:bg-primary/90 text-white text-xs sm:text-sm font-bold px-4 py-2 transition-colors">
            التالي: {nextStage.label} <ArrowLeft className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
